import React from 'react';
import { Link } from 'react-router-dom';
import i18n from 'i18next';

const t = (key) => i18n.t(key) || key;

export let ManageClinicColumns = (callback = {}, pagination) => [
  { dataField: 'id', text: '#', formatter: (cell, row, rowIndex) => (pagination?.page - 1) * pagination?.limit + (rowIndex + 1)   },
  { dataField: 'clinic_name', text: 'Clinic Name' },
  { dataField: 'doctor_name', text: 'Doctor Name' },
  { dataField: 'email', text: 'Email' },
  { dataField: 'phone', text: 'Phone' },
  {
    dataField: 'actions', text: 'Actions', isDummyField: true,
    headerStyle: { textAlign: 'center', width: '100px' },
    style: { textAlign: 'center' },
    formatter: (cell, row) => (
      <div className="table-actions-cell">
        <Link to="#" className="table-action-btn icon-only btn-edit me-1" onClick={() => callback?.edit(row)} title={t("Edit")} >
          <i className="fas fa-pencil-alt" />
        </Link>
        <Link to="#" className={`table-action-btn icon-only ${row.status === 0 ? 'btn-delete' : 'btn-restore'} me-1`} onClick={() => callback?.delete(row.id, row.status === 1 ? 0 : 1, row.code)} title={row.status === 0 ? t("Delete") : t("Restore")} >
          <i className={`fas ${row.status == 0 ? 'fa-trash-alt' : 'fa-check'}`} />
        </Link>
      </div>
    )
  }
];

export let ManageDoctorColumns = (callback = {}, pagination) => [
  { 
    dataField: 'id', 
    text: '#', 
    formatter: (cell, row, rowIndex) => (
      <span className="text-muted font-weight-semibold font-size-12">
        {pagination?.page ? (pagination.page - 1) * (pagination.limit || 10) + (rowIndex + 1) : rowIndex + 1}
      </span>
    ),
    headerStyle: { width: '45px', textAlign: 'center', verticalAlign: 'middle' },
    style: { textAlign: 'center', verticalAlign: 'middle' }
  },
  { 
    dataField: 'name', 
    text: 'Doctor Profile & Contact', 
    headerStyle: { minWidth: '260px', verticalAlign: 'middle' },
    style: { verticalAlign: 'middle' },
    formatter: (cell, row) => {
      const docInitials = (row.name || 'D').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
      const docColor = row.color_code || '#F8CA15';
      return (
        <div className="d-flex align-items-start gap-2.5 py-1">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold shadow-2xs flex-shrink-0 mt-0.5"
            style={{ 
              width: 36, 
              height: 36, 
              backgroundColor: docColor,
              fontSize: '0.85rem'
            }}
          >
            {docInitials}
          </div>
          <div>
            <div className="d-flex align-items-center gap-1.5 flex-wrap">
              <strong className="text-slate-900 font-size-13">{row.name}</strong>
              <span className="badge bg-light text-primary border font-monospace font-size-10 px-1 py-0.5">
                {row.code || `DOC-${row.id}`}
              </span>
            </div>
            
            {/* Contact Info & Registration directly below doctor name */}
            <div className="d-flex align-items-center gap-2 flex-wrap font-size-11 text-slate-600 mt-1">
              {row.mobile && (
                <span className="d-inline-flex align-items-center gap-1">
                  <i className="fas fa-phone-alt text-muted font-size-10" />
                  <a href={`tel:${row.mobile}`} className="text-reset font-weight-medium">{row.mobile}</a>
                </span>
              )}
              {row.email && (
                <span className="d-inline-flex align-items-center gap-1 text-muted">
                  • <i className="far fa-envelope text-muted font-size-10" />
                  <a href={`mailto:${row.email}`} className="text-reset text-truncate" style={{ maxWidth: '170px' }}>{row.email}</a>
                </span>
              )}
              {row.registration_no && (
                <span className="text-muted font-size-10">
                  • {t("Reg")}: <strong>{row.registration_no}</strong>
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }
  },
  { 
    dataField: 'clinic', 
    text: 'Clinic / Branch', 
    headerStyle: { minWidth: '180px', verticalAlign: 'middle' },
    style: { verticalAlign: 'middle' },
    formatter: (cell, row) => (
      <div className="d-flex align-items-center gap-1 py-1">
        <i className="fas fa-hospital-alt text-primary font-size-13 me-1" />
        <span className="font-weight-semibold text-slate-800 font-size-13">
          {row.clinic?.clinic_name || t("Main Clinic")}
        </span>
      </div>
    )
  },
  { 
    dataField: 'allocated_chairs', 
    text: 'Assigned Operatories & Shifts', 
    isDummyField: true,
    headerStyle: { minWidth: '220px', verticalAlign: 'middle' },
    style: { verticalAlign: 'middle' },
    formatter: (cell, row) => {
      const docAllocations = (row.allocated_chairs_list && row.allocated_chairs_list.length > 0)
        ? row.allocated_chairs_list
        : (callback?.allocations || []).filter(a => {
            if (Number(a.status) !== 0) return false;
            const aCode = String(a.doctor_code || a.doctor?.code || '').trim().toLowerCase();
            const rCode = String(row.code || '').trim().toLowerCase();
            if (aCode && rCode && aCode === rCode) return true;

            const aId = String(a.doctor_id || a.doctor?.id || '');
            const rId = String(row.id || '');
            if (aId && rId && aId === rId) return true;

            const aName = String(a.doctor?.name || '').trim().toLowerCase();
            const rName = String(row.name || '').trim().toLowerCase();
            if (aName && rName && aName === rName) return true;

            if (aCode && rId && aCode === rId) return true;
            if (rCode && aId && rCode === aId) return true;

            return false;
          });

      if (!docAllocations || docAllocations.length === 0) {
        return (
          <span className="badge bg-light text-muted border font-size-11 px-2 py-1 font-weight-normal">
            <i className="fas fa-chair me-1 opacity-50" /> {t("No Chair Assigned")}
          </span>
        );
      }

      return (
        <div className="d-flex flex-wrap gap-1 py-1">
          {docAllocations.map((alloc, idx) => {
            const chairTitle = alloc.chair?.title || (alloc.chair_code ? `${t("Chair")} #${alloc.chair_code}` : t("Operatory Chair"));
            const cabinText = alloc.chair?.cabin_no ? `${t("Cabin")} ${alloc.chair.cabin_no}` : (alloc.chair?.title || '');
            const startTimeStr = alloc.start_time?.substring(0, 5);
            const endTimeStr = alloc.end_time?.substring(0, 5);

            return (
              <span 
                key={`row-alloc-${alloc.id || idx}`}
                className="badge px-2 py-1 d-inline-flex align-items-center gap-1 font-size-11 font-weight-semibold"
                style={{
                  backgroundColor: '#e0f2fe',
                  color: '#0369a1',
                  border: '1px solid #bae6fd'
                }}
                title={chairTitle}
              >
                <i className="fas fa-chair font-size-10" />
                <span>{cabinText || chairTitle}</span>
                {startTimeStr && endTimeStr && (
                  <span className="text-muted font-monospace font-size-10">({startTimeStr} - {endTimeStr})</span>
                )}
              </span>
            );
          })}
        </div>
      );
    }
  },
  {
    dataField: 'status',
    text: 'Status',
    headerStyle: { width: '90px', textAlign: 'center', verticalAlign: 'middle' },
    style: { textAlign: 'center', verticalAlign: 'middle' },
    formatter: (cell, row) => (
      <span className={`badge bg-${row.status === 0 ? 'success' : 'danger'}-subtle text-${row.status === 0 ? 'success' : 'danger'} border font-size-11 px-2 py-1`}>
        <i className={`fas fa-circle font-size-8 me-1 ${row.status === 0 ? 'text-success' : 'text-danger'}`} />
        {row.status === 0 ? t("Active") : t("Inactive")}
      </span>
    )
  },
  {
    dataField: 'actions', 
    text: 'Actions', 
    isDummyField: true,
    headerStyle: { width: '140px', textAlign: 'center', verticalAlign: 'middle' },
    style: { textAlign: 'center', verticalAlign: 'middle', width: '140px' },
    formatter: (cell, row) => (
      <div className="table-actions-cell py-1">
        <button
          type="button"
          className="table-action-btn with-text btn-assign me-1"
          onClick={() => callback?.assignChair && callback.assignChair(row)}
          title={t("Assign Operatory Chair & Time Window")}
        >
          <i className="fas fa-chair" />
          <span>{t("Assign")}</span>
        </button>
        <button
          type="button"
          className="table-action-btn icon-only btn-edit me-1"
          onClick={() => callback?.edit(row)}
          title={t("Edit Doctor Profile")}
        >
          <i className="fas fa-pencil-alt" />
        </button>
        <button
          type="button"
          className={`table-action-btn icon-only ${row.status === 0 ? 'btn-delete' : 'btn-restore'} me-1`}
          onClick={() => callback?.delete(row.id, row.status === 1 ? 0 : 1, row.code)}
          title={row.status === 0 ? t("Deactivate Doctor") : t("Activate Doctor")}
        >
          <i className={`fas ${row.status === 0 ? 'fa-trash-alt' : 'fa-check'}`} />
        </button>
      </div>
    )
  }
];
